import Box from "@sproutsocial/seeds-react-box";
import Text from "@sproutsocial/seeds-react-text";
import { needsStyledComponents } from "@sproutsocial/seeds-react-system-props";
import { usePanelContext } from "./PanelContext";
import { PanelCloseButton } from "./PanelCloseButton";
import { cn } from "./PanelShared";
import type { TypePanelHeaderProps } from "./PanelTypes";

/**
 * Styled-components header — the backward-compatible fallback. The hybrid
 * router sends consumers here whenever they pass styled-system props (e.g.
 * `<Panel.Header alignItems="center">`) or a `styled()` extension, so the full
 * `Box` prop surface keeps working.
 */
const PanelHeaderStyled = ({
  title = "",
  id = undefined,
  children,
  render,
  ...rest
}: TypePanelHeaderProps) => (
  <Box
    display="flex"
    flex="0 0 auto"
    justifyContent="space-between"
    alignItems="center"
    p={400}
    borderBottom="1px solid"
    borderColor="container.border.base"
    {...rest}
  >
    {children || (
      <>
        <Text.Headline id={id}>{title}</Text.Headline>
        <PanelCloseButton />
      </>
    )}
  </Box>
);

/**
 * Tailwind/CSS-class header — the preferred path. Renders a plain `<div>` with
 * the `seeds-panel-header` class from panel.css instead of a styled `Box`. The
 * `render` prop is handled by the hybrid before reaching here.
 */
const PanelHeaderTailwind = ({
  title = "",
  id = undefined,
  children,
  render,
  ...rest
}: TypePanelHeaderProps) => {
  const { className, ...domRest } = rest as {
    className?: string;
  } & Record<string, unknown>;

  return (
    <div className={cn("seeds-panel-header", className)} {...domRest}>
      {children || (
        <>
          <Text.Headline id={id}>{title}</Text.Headline>
          <PanelCloseButton />
        </>
      )}
    </div>
  );
};

/**
 * Hybrid header router. The `render` escape hatch short-circuits before any
 * routing (matching the original always-styled behaviour); otherwise it routes
 * on the leftover props after the header's own props are removed.
 */
export const PanelHeader = (props: TypePanelHeaderProps) => {
  const panelContext = usePanelContext();

  if (props.render) {
    return props.render(panelContext);
  }

  const { title, id, children, render, ...rest } = props;

  if (needsStyledComponents(rest)) {
    return <PanelHeaderStyled {...props} />;
  }

  return <PanelHeaderTailwind {...props} />;
};

PanelHeader.displayName = "Panel.Header";
